import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { themeContext } from "../../context/theme";
import { useGamepad } from "../../hooks/useGamepad";
import { playSound, focusOnMouseMove } from "../../utilities/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCog,
  faClock,
  faTrophy,
  faFire,
  faBolt,
  faUsers,
  faCheck,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./CompactGameInfo.module.css";

export const CompactGameInfo = ({
  gameSettings,
  onUpdateSettings,
  isHost,
  game,
  isLoading = false,
  onModalStateChange,
  onModalClosed,
}) => {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [gamepadEnabled, setGamepadEnabled] = useState(false);
  const modalRef = useRef(null);
  const isMouseModeRef = useRef(false);
  const { theme } = useContext(themeContext);

  // Notify parent when modal state changes
  useEffect(() => {
    if (onModalStateChange) {
      onModalStateChange(showSettingsModal);
    }
    // Notify when modal closes
    if (!showSettingsModal && onModalClosed) {
      onModalClosed();
    }
  }, [showSettingsModal, onModalStateChange, onModalClosed]);

  // Enable gamepad with delay to prevent immediate button press
  useEffect(() => {
    if (showSettingsModal) {
      const timer = setTimeout(() => {
        setGamepadEnabled(true);
      }, 200);
      return () => clearTimeout(timer);
    } else {
      setGamepadEnabled(false);
    }
  }, [showSettingsModal]);

  // Extract theme number from theme string (e.g., "theme-0" -> "0")
  const themeNumber = theme.replace("theme-", "");

  // Focusable elements for modal navigation
  const focusableElements = useMemo(() => {
    return { elements: null, index: 0 };
  }, []);

  const gameModes = [
    {
      id: "survival",
      name: "Survival",
      description: "Speed increases gradually • 10 min • last survivor wins",
      icon: faFire,
      timeLimit: 600,
      winCondition: "last_survivor",
      speedMultipliers: {
        0: 3.0,   // 3x at start
        60: 3.5,  // 3.5x at 1 minute
        120: 4.0, // 4x at 2 minutes
        180: 4.5, // 4.5x at 3 minutes
        240: 5.0, // 5x at 4 minutes
        300: 5.5, // 5.5x at 5 minutes
        360: 6.0, // 6x at 6 minutes
        420: 6.5, // 6.5x at 7 minutes
        480: 7.0, // 7x at 8 minutes
        540: 7.5, // 7.5x at 9 minutes
        600: 8.0, // 8x at 10 minutes (max speed)
      },
    },
    {
      id: "speed_battle",
      name: "Speed Score Battle",
      description: "Speed increases gradually • 10 min • highest scorer wins",
      icon: faBolt,
      timeLimit: 600,
      winCondition: "highest_score",
      speedMultipliers: {
        0: 1.5,   // 1.5x at start
        60: 2.0,  // 2x at 1 minute
        120: 2.5, // 2.5x at 2 minutes
        180: 3.0, // 3x at 3 minutes
        240: 3.5, // 3.5x at 4 minutes
        300: 4.0, // 4x at 5 minutes
        360: 4.5, // 4.5x at 6 minutes
        420: 5.0, // 5x at 7 minutes (max speed)
      },
    },
  ];

  const getCurrentMode = () => {
    return (
      gameModes.find((mode) => mode.id === gameSettings.gameMode) ||
      gameModes[0]
    );
  };

  const formatTime = (seconds) => {
    if (!seconds) return "∞";
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m`;
  };

  const handleModeSelect = (mode) => {
    game?.sounds?.clickSound?.play();
    const newSettings = {
      gameMode: mode.id,
      timeLimit: mode.timeLimit,
      winCondition: mode.winCondition,
      speedMultipliers: mode.speedMultipliers || {},
    };
    onUpdateSettings(newSettings);
  };

  const handlePlayerCountSelect = (count) => {
    game?.sounds?.clickSound?.play();
    onUpdateSettings({ maxPlayers: count });
  };

  const currentMode = getCurrentMode();

  // Initialize focusable elements and auto-focus modal when opened
  useEffect(() => {
    if (showSettingsModal && modalRef.current) {
      // Focus the modal container so keyboard events work
      modalRef.current.focus();

      if (gamepadEnabled) {
        setTimeout(() => {
          const elements = modalRef.current.querySelectorAll(
            `.${styles.modeOption}, .${styles.countOption}`
          );
          focusableElements.elements = elements;
          focusableElements.index = 0;
        }, 50);
      }
    } else if (!showSettingsModal) {
      // Reset when modal closes
      focusableElements.index = 0;
    }
  }, [showSettingsModal, gamepadEnabled, focusableElements]);

  // Handle gamepad navigation in modal
  const handleGamepadNavigation = useCallback(
    (direction) => {
      if (!showSettingsModal) return;

      // Switch to keyboard mode
      isMouseModeRef.current = false;

      const modal = document.querySelector(`.${styles.modal}`);
      if (!modal) return;

      const elements = modal.querySelectorAll(
        `.${styles.modeOption}, .${styles.countOption}`
      );
      if (elements.length === 0) return;

      focusableElements.elements = elements;

      if (
        focusableElements.index === undefined ||
        focusableElements.index >= elements.length
      ) {
        focusableElements.index = 0;
      }

      if (direction === "down" || direction === "right") {
        if (focusableElements.index >= elements.length - 1) {
          focusableElements.index = 0;
        } else {
          focusableElements.index++;
        }
      } else if (direction === "up" || direction === "left") {
        if (focusableElements.index <= 0) {
          focusableElements.index = elements.length - 1;
        } else {
          focusableElements.index--;
        }
      }

      if (elements[focusableElements.index]) {
        playSound(game, "navigation");
        elements[focusableElements.index].focus();
      }
    },
    [showSettingsModal, focusableElements, game]
  );

  // Handle gamepad button presses in modal
  const handleGamepadButtonPress = useCallback(
    (button) => {
      if (!showSettingsModal) return;

      if (button === "X") {
        const modal = document.querySelector(`.${styles.modal}`);
        if (!modal) return;

        const elements = modal.querySelectorAll(
          `.${styles.modeOption}, .${styles.countOption}`
        );
        if (elements[focusableElements.index]) {
          elements[focusableElements.index].click();
          game?.sounds?.clickSound?.play();
        }
      } else if (button === "B" || button === "start") {
        setShowSettingsModal(false);
        game?.sounds?.clickSound?.play();
      }
    },
    [showSettingsModal, focusableElements, game]
  );

  // Use the useGamepad hook for modal
  useGamepad({
    onNavigate: handleGamepadNavigation,
    onButtonPress: handleGamepadButtonPress,
    navigationDelay: 150,
    buttonDelay: 150,
    enabled: gamepadEnabled,
    gamepadIndex: null,
  });

  return (
    <>
      {/* Compact Inline Display */}
      <div className={styles.compactInfo}>
        <div className={styles.gameInfo}>
          <FontAwesomeIcon
            icon={currentMode.icon}
            className={`${styles.modeIcon} ${
              styles[`modeIcon-theme-${themeNumber}`]
            }`}
          />
          <span className={styles.modeName}>{currentMode.name}</span>
          <span className={styles.separator}>•</span>
          <FontAwesomeIcon
            icon={faUsers}
            className={`${styles.infoIcon} ${
              styles[`infoIcon-theme-${themeNumber}`]
            }`}
          />
          <span className={styles.infoText}>{gameSettings.maxPlayers}</span>
          <span className={styles.separator}>•</span>
          <FontAwesomeIcon
            icon={faClock}
            className={`${styles.infoIcon} ${
              styles[`infoIcon-theme-${themeNumber}`]
            }`}
          />
          <span className={styles.infoText}>
            {formatTime(gameSettings.timeLimit)}
          </span>
        </div>
        {isHost && (
          <button
            className={`${styles.settingsButton} ${
              styles[`settingsButton-theme-${themeNumber}`]
            }`}
            onClick={() => {
              game?.sounds?.clickSound?.play();
              setShowSettingsModal(true);
            }}
            title="Configure game settings"
            tabIndex="0"
          >
            <FontAwesomeIcon icon={faCog} />
          </button>
        )}
      </div>

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className={styles.modalOverlay}>
          <div
            ref={modalRef}
            className={`${styles.modal} ${
              styles[`modal-theme-${themeNumber}`]
            }`}
            onMouseMove={(event) => {
              if (isMouseModeRef.current !== false) {
                // Only process if not in keyboard mode
                // Switch to mouse mode
                isMouseModeRef.current = true;

                const modal = event.currentTarget;
                const elements = modal.querySelectorAll(
                  `.${styles.modeOption}, .${styles.countOption}`
                );
                if (elements.length > 0) {
                  focusableElements.elements = elements;
                  // When hovering a new element, blur ALL focused elements (mouse mode)
                  focusableElements?.elements?.forEach((element, index) => {
                    if (
                      event.target === element &&
                      index !== focusableElements.index
                    ) {
                      // Blur all elements when in mouse mode
                      focusableElements.elements.forEach((el) => {
                        if (el === document.activeElement) {
                          el.blur();
                        }
                      });
                      focusableElements.index = index;
                    }
                  });
                }
              }
            }}
            onKeyDown={(event) => {
              // Switch to keyboard mode
              isMouseModeRef.current = false;

              const modal = event.currentTarget;
              const elements = modal.querySelectorAll(
                `.${styles.modeOption}, .${styles.countOption}`
              );
              if (elements.length === 0) return;

              focusableElements.elements = elements;

              if (
                focusableElements.index === undefined ||
                focusableElements.index >= elements.length
              ) {
                focusableElements.index = 0;
              }

              if (event.key === "ArrowDown") {
                event.preventDefault();
                if (focusableElements.index >= elements.length - 1) {
                  focusableElements.index = 0;
                } else {
                  focusableElements.index++;
                }
                if (elements[focusableElements.index]) {
                  playSound(game, "navigation");
                  elements[focusableElements.index].focus();
                }
              } else if (event.key === "ArrowUp") {
                event.preventDefault();
                if (focusableElements.index <= 0) {
                  focusableElements.index = elements.length - 1;
                } else {
                  focusableElements.index--;
                }
                if (elements[focusableElements.index]) {
                  playSound(game, "navigation");
                  elements[focusableElements.index].focus();
                }
              } else if (event.key === "Enter") {
                event.preventDefault();
                if (elements[focusableElements.index]) {
                  elements[focusableElements.index].click();
                }
              } else if (event.key === "Escape") {
                event.preventDefault();
                setShowSettingsModal(false);
                game?.sounds?.clickSound?.play();
              }
            }}
            tabIndex={0}
          >
            <div
              className={`${styles.modalHeader} ${
                styles[`modalHeader-theme-${themeNumber}`]
              }`}
            >
              <h3 className={styles.modalTitle}>
                <FontAwesomeIcon
                  icon={faCog}
                  className={`${styles.modalIcon} ${
                    styles[`modalIcon-theme-${themeNumber}`]
                  }`}
                />
                Game Settings
              </h3>
              <button
                className={`${styles.closeButton} ${
                  styles[`closeButton-theme-${themeNumber}`]
                }`}
                onClick={() => setShowSettingsModal(false)}
                tabIndex="-1"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className={styles.modalContent}>
              {/* Game Mode Selection */}
              <div className={styles.settingSection}>
                <h4 className={styles.sectionTitle}>
                  <FontAwesomeIcon
                    icon={currentMode.icon}
                    className={`${styles.sectionIcon} ${
                      styles[`sectionIcon-theme-${themeNumber}`]
                    }`}
                  />
                  Game Mode
                </h4>
                <div className={styles.optionGrid}>
                  {gameModes.map((mode) => (
                    <button
                      key={mode.id}
                      className={styles.modeOption}
                      onClick={() => handleModeSelect(mode)}
                      disabled={isLoading}
                      tabIndex="0"
                    >
                      <FontAwesomeIcon
                        icon={mode.icon}
                        className={`${styles.optionIcon} ${
                          styles[`optionIcon-theme-${themeNumber}`]
                        }`}
                      />
                      <div className={styles.optionInfo}>
                        <span className={styles.optionName}>{mode.name}</span>
                        <span className={styles.optionMeta}>
                          {mode.description}
                        </span>
                      </div>
                      {gameSettings.gameMode === mode.id && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className={`${styles.checkIcon} ${
                            styles[`checkIcon-theme-${themeNumber}`]
                          }`}
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Player Count Selection */}
              <div className={styles.settingSection}>
                <h4 className={styles.sectionTitle}>
                  <FontAwesomeIcon
                    icon={faUsers}
                    className={`${styles.sectionIcon} ${
                      styles[`sectionIcon-theme-${themeNumber}`]
                    }`}
                  />
                  Max Players
                </h4>
                <div className={styles.playerCountOptions}>
                  {[2, 3, 4].map((count) => (
                    <button
                      key={count}
                      className={styles.countOption}
                      onClick={() => handlePlayerCountSelect(count)}
                      disabled={isLoading}
                      tabIndex="0"
                    >
                      {gameSettings.maxPlayers === count && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className={`${styles.countCheckIcon} ${
                            styles[`countCheckIcon-theme-${themeNumber}`]
                          }`}
                        />
                      )}
                      {count}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
